import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import {
  GET_HUBRISE_CATALOG,
  PUSH_CATALOG_TO_HUBRISE,
} from '../../graphql/hubrise-mutations'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import type { HubRiseCatalog, Selection } from './types'
import LeftNav from './components/LeftNav'
import DetailPane from './components/DetailPane'

type PageTab = 'catalog' | 'update' | 'commission' | 'openingHours' | 'preparationTimes'

const PAGE_TABS: { key: PageTab; label: string }[] = [
  { key: 'catalog', label: 'Catalog' },
  { key: 'update', label: 'Update Platforms' },
  { key: 'commission', label: 'Commission' },
  { key: 'openingHours', label: 'Opening Hours' },
  { key: 'preparationTimes', label: 'Preparation Times' },
]

interface PushResult {
  success: boolean
  catalogId?: string | null
  productCount?: number | null
  skuCount?: number | null
  error?: string | null
}

const ExternalPlatformsPage = () => {
  const [pageTab, setPageTab] = useState<PageTab>('catalog')

  // Browse-catalog state
  const [tab, setTab] = useState<'products' | 'optionLists'>('products')
  const [selection, setSelection] = useState<Selection>({ kind: 'none' })
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set())
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null)

  const { data, loading, error, refetch } = useQuery<{ hubriseCatalog: HubRiseCatalog }>(
    GET_HUBRISE_CATALOG,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onCompleted: () => setLastRefreshed(new Date()),
      onError: (err) => toast.error(err.message || 'Failed to fetch HubRise catalog'),
    }
  )

  const catalog = data?.hubriseCatalog

  const optionListsById = useMemo(() => {
    const m = new Map<string, HubRiseCatalog['optionLists'][number]>()
    if (catalog) for (const ol of catalog.optionLists) m.set(ol.id, ol)
    return m
  }, [catalog])

  useEffect(() => {
    if (!catalog || selection.kind !== 'none') return
    const firstCat = catalog.categories.find((c) =>
      catalog.products.some((p) => p.categoryId === c.id)
    )
    if (!firstCat) return
    const firstProduct = catalog.products.find((p) => p.categoryId === firstCat.id)
    if (!firstProduct) return
    setExpandedCategories(new Set([firstCat.id]))
    setSelection({ kind: 'product', productId: firstProduct.id })
  }, [catalog, selection.kind])

  const toggleCategory = (id: string) =>
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleProduct = (id: string) =>
    setExpandedProducts((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleRefresh = () => {
    refetch().catch(() => {})
  }

  // Update-platforms state
  const { hasPermission } = usePermissions()
  const canManage = hasPermission(PERMISSIONS.HUBRISE_MANAGE)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [lastResult, setLastResult] = useState<PushResult | null>(null)
  const [pushCatalog, { loading: pushLoading }] = useMutation<{
    pushCatalogToHubRise: PushResult
  }>(PUSH_CATALOG_TO_HUBRISE)

  const handleConfirm = async () => {
    setConfirmOpen(false)
    try {
      const { data } = await pushCatalog()
      const result = data?.pushCatalogToHubRise
      if (!result) {
        toast.error('No response from server')
        return
      }
      setLastResult(result)
      if (result.success) {
        toast.success(
          `Menu synced: ${result.productCount ?? 0} products, ${result.skuCount ?? 0} SKUs`
        )
      } else {
        toast.error(result.error || 'Catalog push failed')
      }
    } catch (err: any) {
      const message = err?.message || 'Network error while pushing catalog'
      setLastResult({ success: false, error: message })
      toast.error(message)
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] -m-6 bg-white border border-secondary-200 rounded-lg overflow-hidden">
      {/* Page-level tabs */}
      <div className="flex border-b border-secondary-200 bg-white">
        {PAGE_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setPageTab(t.key)}
            className={`px-6 py-3 text-sm font-medium ${
              pageTab === t.key
                ? 'border-b-2 border-primary-500 text-primary-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {pageTab === 'catalog' && (
        <div className="flex-1 flex min-h-0">
          {catalog && (
            <LeftNav
              catalog={catalog}
              tab={tab}
              onTabChange={setTab}
              selection={selection}
              onSelect={setSelection}
              expandedCategories={expandedCategories}
              toggleCategory={toggleCategory}
              expandedProducts={expandedProducts}
              toggleProduct={toggleProduct}
            />
          )}

          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex items-center justify-between px-6 py-3 border-b border-secondary-200 bg-white">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">HubRise Catalog</h1>
                <div className="text-xs text-gray-500">
                  {loading && !catalog
                    ? 'Fetching…'
                    : catalog
                    ? `Catalog ${catalog.id} · ${catalog.products.length} products · ${catalog.optionLists.length} option lists`
                    : 'No catalog loaded'}
                  {lastRefreshed && (
                    <> &nbsp;·&nbsp; last refreshed {lastRefreshed.toLocaleTimeString()}</>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={loading}
                className="btn-secondary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon
                  className={`h-5 w-5 mr-2 ${loading ? 'animate-spin' : ''}`}
                />
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {error && !catalog && (
                <div className="p-3 rounded-md text-sm bg-red-50 text-red-800 border border-red-200">
                  <div className="font-medium">Failed to fetch catalog</div>
                  <div className="mt-1 whitespace-pre-wrap">{error.message}</div>
                </div>
              )}
              {!catalog && loading && (
                <div className="text-sm text-gray-500">Loading HubRise catalog…</div>
              )}
              {catalog && (
                <DetailPane
                  catalog={catalog}
                  selection={selection}
                  optionListsById={optionListsById}
                  onSelectSku={(productId, skuId) => {
                    setExpandedProducts((prev) => new Set(prev).add(productId))
                    setSelection({ kind: 'sku', productId, skuId })
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {pageTab === 'update' && (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="card max-w-3xl">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-3 rounded-lg bg-primary-500">
                <GlobeAltIcon className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">HubRise Catalog</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Sync the POS menu to Uber Eats and Thuisbezorgd via HubRise. Use this
                  after making changes to products, prices, markups, or meal deals.
                </p>
                <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                  Only run this once after you're done making changes. Do not click
                  repeatedly — each sync rebuilds the full catalog and can take up to a
                  minute.
                </p>

                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setConfirmOpen(true)}
                    disabled={pushLoading || !canManage}
                    title={!canManage ? 'Requires hubrise.manage permission' : undefined}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <ArrowPathIcon
                      className={`h-5 w-5 mr-2 ${pushLoading ? 'animate-spin' : ''}`}
                    />
                    {pushLoading
                      ? 'Syncing... (this can take up to a minute)'
                      : 'Sync platforms'}
                  </button>
                  {!canManage && (
                    <p className="mt-2 text-xs text-gray-500">
                      You have view-only access. Pushing the catalog requires the{' '}
                      <span className="font-mono">hubrise.manage</span> permission.
                    </p>
                  )}
                </div>

                {lastResult && !pushLoading && (
                  <div
                    className={`mt-4 p-3 rounded-md text-sm ${
                      lastResult.success
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                  >
                    {lastResult.success ? (
                      <>
                        <div className="font-medium">Last sync successful</div>
                        <div className="mt-1">
                          {lastResult.productCount ?? 0} products ·{' '}
                          {lastResult.skuCount ?? 0} SKUs
                          {lastResult.catalogId && (
                            <span className="text-green-700">
                              {' '}
                              · catalog {lastResult.catalogId}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-medium">Last sync failed</div>
                        <div className="mt-1 whitespace-pre-wrap">{lastResult.error}</div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {pageTab === 'commission' && (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-sm text-gray-500">Commission — coming soon.</div>
        </div>
      )}

      {pageTab === 'openingHours' && (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-sm text-gray-500">Opening Hours — coming soon.</div>
        </div>
      )}

      {pageTab === 'preparationTimes' && (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-sm text-gray-500">Preparation Times — coming soon.</div>
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-gray-900 bg-opacity-50"
            onClick={() => setConfirmOpen(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 p-2 rounded-full bg-yellow-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Sync platforms?</h3>
                <p className="mt-2 text-sm text-gray-600">
                  This will push the current POS menu to HubRise and replace the active
                  catalog on Uber Eats and Thuisbezorgd. The process can take up to a
                  minute — please wait for it to finish and do not trigger it again right
                  after. Are you sure you want to continue?
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button type="button" onClick={handleConfirm} className="btn-primary">
                Yes, sync now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExternalPlatformsPage
